import {
  type ReviewRouteInput,
  type ReviewRouteView,
  type RevisionReviewView,
  SHEET_REVIEW_RESULTS,
  type SheetReviewResult,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

/**
 * Проверка комплекта по маршруту (ПД-25): маршруты проекта на вкладке «Правило шифра и маршруты» и ход
 * проверки ревизии на карточке комплекта с решениями проверяющего по листам.
 */

type People = Array<{ id: string; fullName: string; external: boolean }>;

const STATE = {
  waiting: { label: 'ждёт', tone: undefined },
  current: { label: 'идёт', tone: 'info' },
  done: { label: 'пройден', tone: 'success' },
  rejected: { label: 'отклонено', tone: 'danger' },
} as const;

const OUTCOME = {
  passed: { label: 'принят', tone: 'success' },
  rejected: { label: 'отклонён', tone: 'danger' },
  pending: { label: '—', tone: undefined },
} as const;

export function RevisionReviewPanel({ revisionId, setId }: { revisionId: string; setId: string }) {
  const qc = useQueryClient();
  const me = useMe().user.id;
  const q = useQuery({
    queryKey: ['psd-review', revisionId],
    queryFn: () => api<RevisionReviewView | null>(`/psd/revisions/${revisionId}/review`),
    retry: false,
  });
  const [draft, setDraft] = useState<Record<string, { result: SheetReviewResult; comment: string }>>({});
  const m = useMutation({
    mutationFn: () =>
      api<{ status: string; step: string }>(`/psd/revisions/${revisionId}/review`, {
        body: {
          results: Object.entries(draft).map(([sheetId, v]) => ({
            sheetId,
            result: v.result,
            comment: v.comment.trim() || null,
          })),
        },
      }),
    onSuccess: (r) => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ['psd-review', revisionId] });
      qc.invalidateQueries({ queryKey: ['psd-set', setId] });
      toast(
        r.status === 'passed'
          ? 'Маршрут пройден — ревизия утверждена'
          : r.status === 'rejected'
            ? 'Ревизия возвращена с замечаниями'
            : `Решения сохранены · шаг «${r.step}»`,
      );
    },
  });
  if (q.isError)
    return (
      <Panel title="Проверка по маршруту">
        <ErrorBox error={q.error} />
      </Panel>
    );
  if (!q.data) return null;
  const v = q.data;
  const current = v.steps.find((s) => s.id === v.currentStepId);
  const missingComment = Object.values(draft).some((d) => d.result !== 'accepted' && !d.comment.trim());
  return (
    <Panel
      title={`Проверка по маршруту «${v.routeName}»`}
      meta={
        <Badge tone={v.status === 'passed' ? 'success' : v.status === 'rejected' ? 'danger' : 'info'}>
          {v.status === 'passed' ? 'пройдена' : v.status === 'rejected' ? 'с замечаниями' : 'идёт'}
        </Badge>
      }
    >
      <ol className="mb-3 flex flex-col gap-1.5 text-[14px]">
        {v.steps.map((s, i) => (
          <li key={s.id} className="flex flex-wrap items-center gap-1.5">
            <span className="num text-ink-3">{i + 1}.</span>
            <span className="font-medium">{s.name}</span>
            <Badge tone={STATE[s.state].tone}>{STATE[s.state].label}</Badge>
            <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
              любые {s.required} из {s.reviewers.length}: {s.reviewers.map((r) => r.fullName).join(', ')}
            </span>
          </li>
        ))}
      </ol>
      {v.canReview && current ? (
        <p className="text-[13px] text-ink-2">
          Ваши решения на шаге «{current.name}»: каждому листу — принят, принят с замечаниями или отклонён.
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-line">
        {v.sheets.map((sh) => {
          const d = draft[sh.sheetId];
          const curResults = sh.results.filter((r) => r.stepId === v.currentStepId);
          // Уже сохранённое решение проверяющего — в поле, чтобы было видно, что лист решён
          const saved = curResults.find((r) => r.userId === me);
          const eff = d ?? (saved ? { result: saved.result, comment: saved.comment ?? '' } : undefined);
          return (
            <li key={sh.sheetId} className="flex flex-col gap-1.5 py-2.5 text-[14px]">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="num font-medium">Лист {sh.number}</span>
                <span className="text-ink-2 [overflow-wrap:anywhere]">{sh.name}</span>
                {v.steps.map((s) =>
                  sh.outcome[s.id] !== 'pending' ? (
                    <Badge key={s.id} tone={OUTCOME[sh.outcome[s.id] ?? 'pending'].tone}>
                      {s.name}: {OUTCOME[sh.outcome[s.id] ?? 'pending'].label}
                    </Badge>
                  ) : null,
                )}
              </div>
              {curResults.length || sh.results.some((r) => r.result !== 'accepted') ? (
                <ul className="text-[13px] text-ink-2">
                  {sh.results
                    .filter((r) => r.stepId === v.currentStepId || r.result !== 'accepted')
                    .map((r) => (
                      <li key={`${r.stepId}-${r.userId}`} className="[overflow-wrap:anywhere]">
                        {r.fullName}: {SHEET_REVIEW_RESULTS[r.result].toLowerCase()}
                        {r.comment ? ` — ${r.comment}` : ''}
                      </li>
                    ))}
                </ul>
              ) : null}
              {v.canReview ? (
                <div className="flex flex-col gap-1.5 md:flex-row">
                  <Select
                    aria-label={`Решение по листу ${sh.number}`}
                    className="h-11 md:h-9 md:w-56"
                    value={d?.result ?? saved?.result ?? ''}
                    onChange={(e) =>
                      setDraft((x) => {
                        const n = { ...x };
                        if (!e.target.value) delete n[sh.sheetId];
                        else
                          n[sh.sheetId] = {
                            result: e.target.value as SheetReviewResult,
                            comment: x[sh.sheetId]?.comment ?? saved?.comment ?? '',
                          };
                        return n;
                      })
                    }
                  >
                    <option value="">— решение —</option>
                    {(Object.keys(SHEET_REVIEW_RESULTS) as SheetReviewResult[]).map((k) => (
                      <option key={k} value={k}>
                        {SHEET_REVIEW_RESULTS[k]}
                      </option>
                    ))}
                  </Select>
                  {eff && eff.result !== 'accepted' ? (
                    <Input
                      aria-label={`Замечание к листу ${sh.number}`}
                      className="h-11 md:h-9 md:flex-1"
                      placeholder="Что не так (обязательно)"
                      value={eff.comment}
                      onChange={(e) =>
                        setDraft((x) => ({ ...x, [sh.sheetId]: { ...eff, comment: e.target.value } }))
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {v.canReview ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            className="h-11 md:h-10"
            onClick={() =>
              setDraft(
                Object.fromEntries(
                  v.sheets.map((sh) => [sh.sheetId, { result: 'accepted' as const, comment: '' }]),
                ),
              )
            }
          >
            Принять все листы
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!Object.keys(draft).length || missingComment}
            onClick={() => m.mutate()}
          >
            Сохранить решения
          </Button>
          <ErrorBox error={m.error} />
        </div>
      ) : null}
    </Panel>
  );
}

export function ReviewRoutesPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['psd-review-routes', projectId],
    queryFn: () => api<ReviewRouteView[]>(`/psd/projects/${projectId}/review-routes`),
    retry: false,
  });
  const [edit, setEdit] = useState<ReviewRouteView | 'new' | null>(null);
  const off = useMutation({
    mutationFn: (id: string) => api(`/psd/review-routes/${id}/deactivate`, { body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['psd-review-routes', projectId] });
      toast('Маршрут отключён');
    },
  });
  if (q.isError) return null;
  const list = (q.data ?? []).filter((r) => r.active);
  return (
    <Panel
      title="Маршруты проверки комплектов"
      actions={
        <Button className="h-11 md:h-9" icon={<Plus className="size-4" />} onClick={() => setEdit('new')}>
          Новый маршрут
        </Button>
      }
    >
      {list.length ? (
        <ul className="flex flex-col gap-3 text-[14px]">
          {list.map((r) => (
            <li key={r.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{r.name}</span>
                <span className="text-[13px] text-ink-2">
                  {r.marks.length ? `марки ${r.marks.join(', ')}` : 'все марки'}
                  {r.stages.length ? ` · стадии ${r.stages.join(', ')}` : ''}
                </span>
              </div>
              <ol className="text-[13px] text-ink-2">
                {r.steps.map((s, i) => (
                  <li key={s.id} className="[overflow-wrap:anywhere]">
                    {i + 1}. {s.name} — любые {s.required} из {s.reviewers.length}:{' '}
                    {s.reviewers.map((x) => x.fullName).join(', ')}
                  </li>
                ))}
              </ol>
              <div className="flex gap-2">
                <Button variant="ghost" className="h-11 md:h-9" onClick={() => setEdit(r)}>
                  Изменить
                </Button>
                <Button
                  variant="ghost"
                  className="h-11 md:h-9"
                  loading={off.isPending && off.variables === r.id}
                  onClick={() => off.mutate(r.id)}
                >
                  Отключить
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">
          Маршрут запускается сам, когда ревизия комплекта нужной марки и стадии уходит на проверку: каждый
          шаг — «любые N из назначенных», каждый лист получает решение. Без маршрута ревизию утверждает ГИП
          вручную.
        </p>
      )}
      <ErrorBox error={off.error} />
      {edit ? (
        <RouteDialog
          projectId={projectId}
          route={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
        />
      ) : null}
    </Panel>
  );
}

type Step = { name: string; required: number; reviewerIds: string[] };

function RouteDialog({
  projectId,
  route,
  onClose,
}: {
  projectId: string;
  route: ReviewRouteView | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const people = useQuery({
    queryKey: ['psd-people', projectId],
    queryFn: () => api<People>(`/psd/projects/${projectId}/people`),
  });
  const [name, setName] = useState(route?.name ?? '');
  const [marks, setMarks] = useState(route?.marks.join(', ') ?? '');
  const [stages, setStages] = useState(route?.stages.join(', ') ?? '');
  const [steps, setSteps] = useState<Step[]>(
    route?.steps.map((s) => ({
      name: s.name,
      required: s.required,
      reviewerIds: s.reviewers.map((r) => r.id),
    })) ?? [{ name: 'ПТО', required: 1, reviewerIds: [] }],
  );
  const split = (v: string) =>
    v
      .split(/[,;\s]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  const body: ReviewRouteInput = { name: name.trim(), marks: split(marks), stages: split(stages), steps };
  const m = useMutation({
    mutationFn: () =>
      route
        ? api(`/psd/review-routes/${route.id}`, { method: 'PUT', body })
        : api(`/psd/projects/${projectId}/review-routes`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['psd-review-routes', projectId] });
      toast(route ? 'Маршрут изменён — новые проверки пойдут по нему' : 'Маршрут сохранён');
      onClose();
    },
  });
  const setStep = (i: number, patch: Partial<Step>) =>
    setSteps((x) => x.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const valid =
    name.trim().length >= 2 &&
    steps.length > 0 &&
    steps.every((s) => s.name.trim().length >= 2 && s.reviewerIds.length >= s.required && s.required >= 1);
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title={route ? `Маршрут «${route.name}»` : 'Новый маршрут проверки'}
      description="Идущие проверки продолжатся по прежнему маршруту; новый вступает для следующих отправок на проверку."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!valid}
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Название">
          <Input
            className="h-11 md:h-10"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="КЖ: ПТО → сметчик → ГИП"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Марки" hint="Через запятую; пусто — все">
            <Input
              className="h-11 md:h-10"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              placeholder="КЖ, КМ"
            />
          </Field>
          <Field label="Стадии" hint="Через запятую; пусто — все">
            <Input
              className="h-11 md:h-10"
              value={stages}
              onChange={(e) => setStages(e.target.value)}
              placeholder="РП"
            />
          </Field>
        </div>
        {steps.map((s, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: шаги маршрута — по порядку, без своих номеров до сохранения
          <div key={i} className="flex flex-col gap-2 rounded-md border border-line p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Field label={`Шаг ${i + 1}`} className="flex-1">
                <Input
                  className="h-11 md:h-10"
                  value={s.name}
                  onChange={(e) => setStep(i, { name: e.target.value })}
                />
              </Field>
              <Field label="Сколько решений нужно">
                <Input
                  type="number"
                  min={1}
                  max={20}
                  className="h-11 md:h-10 sm:w-28"
                  value={s.required}
                  onChange={(e) => setStep(i, { required: Math.max(1, Number(e.target.value) || 1) })}
                />
              </Field>
              {steps.length > 1 ? (
                <Button
                  variant="ghost"
                  className="h-11 md:h-10"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => setSteps((x) => x.filter((_, j) => j !== i))}
                >
                  Убрать
                </Button>
              ) : null}
            </div>
            <div className="grid max-h-40 gap-1 overflow-auto sm:grid-cols-2">
              {(people.data ?? []).map((p) => (
                <Checkbox
                  key={p.id}
                  className="min-h-11 items-center md:min-h-0"
                  checked={s.reviewerIds.includes(p.id)}
                  onChange={(e) =>
                    setStep(i, {
                      reviewerIds: e.target.checked
                        ? [...s.reviewerIds, p.id]
                        : s.reviewerIds.filter((x) => x !== p.id),
                    })
                  }
                  label={`${p.fullName}${p.external ? ' (внешний)' : ''}`}
                />
              ))}
            </div>
            <p className="text-[12px] text-ink-3">
              Любые {s.required} из {s.reviewerIds.length} назначенных
              {s.reviewerIds.length < s.required ? ' — назначьте больше проверяющих' : ''}
            </p>
          </div>
        ))}
        {steps.length < 10 ? (
          <div>
            <Button
              className="h-11 md:h-10"
              icon={<Plus className="size-4" />}
              onClick={() => setSteps((x) => [...x, { name: '', required: 1, reviewerIds: [] }])}
            >
              Добавить шаг
            </Button>
          </div>
        ) : null}
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}
